/**
 * VueDomToImage: Vue plugin - v0.8.5 (c) 2019-2021; aburai
 *
 * @version 0.8.5
 * @author André Bunse (aburai@github.com) <andre.bunse@gmail.com>
 */
import _Vue from 'vue'
import _ from 'lodash'
import domToImage from 'dom-to-image'
import fileSaver from 'file-saver'
import pdfMake from 'pdfmake/build/pdfmake'
import axios from 'axios'

pdfMake.vfs = {}
try {
    Promise.all([
        axios.get('/fonts/DIN-regular.woff2', {responseType: 'arraybuffer'}),
        axios.get('/fonts/DIN-bold.woff2', {responseType: 'arraybuffer'}),
        axios.get('/fonts/DIN-regular-italic.woff2', {responseType: 'arraybuffer'}),
        axios.get('/fonts/DIN-bold-italic.woff2', {responseType: 'arraybuffer'})
    ]).then(response => {
        pdfMake.vfs['DIN-regular.woff2'] = Buffer.from(response[0].data, 'binary').toString('base64')
        pdfMake.vfs['DIN-bold.woff2'] = Buffer.from(response[1].data, 'binary').toString('base64')
        pdfMake.vfs['DIN-regular-italic.woff2'] = Buffer.from(response[2].data, 'binary').toString('base64')
        pdfMake.vfs['DIN-bold-italic.woff2'] = Buffer.from(response[3].data, 'binary').toString('base64')
    })
}
catch (e) {
    console.error(e)
}
pdfMake.fonts = {
    DIN: {
        normal: 'DIN-regular.woff2',
        bold: 'DIN-bold.woff2',
        italics: 'DIN-regular-italic.woff2',
        bolditalics: 'DIN-bold-italic.woff2'
    }
}

const _DEFAULT_IMAGE_FILENAME = 'dti.png'
const _DEFAULT_PDF_FILENAME = 'dti.pdf'

// get DOM node from given selector
// defaults to vm.$el
const _getNode = function (selector) {
    let node

    if (selector instanceof _Vue) node = selector.$el
    else if (_.isElement(selector)) node = selector
    else if (_.isString(selector)) node = document.querySelectorAll(selector)

    if (!(node instanceof NodeList) && !_.isElement(node)) {
        console.warn('VueDomToImage: invalid selector argument or missing this.$el')
        node = null
    }

    return node
}

const _pushSelectorPromises = function (selector, promises, dark) {
    const options = dark ? {bgcolor: '#121212'} : {bgcolor: '#FFF'}
    // must be a valid DOM element
    // must have width and height
    const _validateNode = node => {
        if (!_.isElement(node)) {
            console.warn('invalid node', node)
            return false
        }
        // TODO improvement for "is visible" state?
        const {width, height} = node.getBoundingClientRect()
        return width && height
    }
    const _push = node => {
        promises.push(domToImage.toPng(node, options))
        // NOTE toJpeg -> bad output
        // promises.push(domToImage.toJpeg(node, {quality: 0.95}))
    }

    // array of NodeList to support sorted output
    if (_.isArray(selector)) {
        _.each(selector, nodeList => {
            _.each(nodeList, el => {
                if (_validateNode(el)) {
                    _push(el)
                }
            })
        })
    }
    // single list of nodes
    else if (selector instanceof NodeList) {
        _.each(selector, el => {
            if (_validateNode(el)) {
                _push(el)
            }
        })
    }
    // other selector to find node(s)
    else {
        const node = _getNode(selector)
        if (!node) return

        if (node instanceof NodeList) {
            _.each(node, el => {
                if (_validateNode(el)) {
                    _push(el)
                }
            })
        } else {
            if (_validateNode(node)) {
                _push(node)
            }
        }
    }

    return true
}

const _imageToDataURL = function (src) {
    return new Promise(resolve => {
        if (!src) return resolve()

        const img = new Image()
        img.onload = function () {
            const canvas = document.createElement('canvas')
            const context = canvas.getContext('2d')
            canvas.width = img.width
            canvas.height = img.height
            if (context) context.drawImage(img, 0, 0, img.width, img.height)
            resolve(canvas.toDataURL('image/png'))
        }
        img.src = src
    })
}

// A4: [595.28, 841.89] see /node_modules/pdfmake/src/standardPageSizes.js
const VueDomToImage = {
    install(Vue, options) {
        // create blob from node
        // save blob with given filename or use default
        Vue.prototype.$domToImage = function (selector, filename) {
            filename = filename || _DEFAULT_IMAGE_FILENAME

            return new Promise((resolve, reject) => {
                const _nextTick = typeof this.$nextTick === 'function' ? this.$nextTick : (fn) => {
                    _.defer(fn)
                }
                _nextTick(() => {
                    let node = _getNode(selector)
                    if (!node) return reject()

                    if (node instanceof NodeList) node = node[0] // image only use a single node
                    // options -> https://github.com/tsayen/dom-to-image
                    const options = this.$vuetify.theme.dark ? {bgcolor: '#121212'} : {bgcolor: '#FFF'}
                    domToImage
                        .toBlob(node, options)
                        .then(blob => {
                            try {
                                fileSaver.saveAs(blob, filename)
                            } catch (e) {
                                // something went wrong!
                                console.log('VueDomToImage: fileSaver.saveAs', e)
                            }
                        })
                        .catch((error) => console.error('VueDomToImage: $domToImage', error))
                        .finally(resolve)
                })
            })
        }

        // create base64 encoded data URL
        // create pdf with image
        // TODO images are not splitted over pages and will be cut!!
        Vue.prototype.$domToPDF = function (selector, options = {}, filename) {
            if (_.isString(options) && !filename) {
                filename = options
                options = {}
            }
            filename = filename || _DEFAULT_PDF_FILENAME

            const _buildDocDefinition = (logo, dataUrls) => {
                const docDefinition = {
                    pageSize: 'A4',
                    compress: false,
                    content: [],
                    defaultStyle: {
                        font: 'DIN'
                    }
                }

                if (options.landscape) docDefinition.pageOrientation = 'landscape'
                if (options.watermark) docDefinition.watermark = options.watermark
                if (options.header) docDefinition.header = options.header
                if (options.footer) {
                    const columns = []
                    if (options.footer.left) columns.push({text: options.footer.left, margin: [16, 8], fontSize: 10})
                    if (options.footer.right) columns.push({
                        text: options.footer.right,
                        alignment: 'right',
                        margin: [16, 8],
                        fontSize: 10
                    })
                    docDefinition.footer = {columns}
                }

                if (logo) {
                    docDefinition.content.push({
                        image: logo,
                        width: 32,
                        alignment: 'right'
                    })
                }

                if (options.title) {
                    docDefinition.content.push({
                        text: options.title,
                        margin: 0,
                        fontSize: 20
                    })
                }
                if (options.subtitle) {
                    docDefinition.content.push({
                        text: options.subtitle,
                        margin: 0,
                        fontSize: 16
                    })
                }

                dataUrls.forEach(dataUrl => {
                    docDefinition.content.push({
                        // you'll most often use dataURI images on the browser side
                        // if no width/height/fit is provided, the original size will be used
                        image: dataUrl,
                        width: options.landscape ? 800 : 560,
                        margin: 8,
                        alignment: 'center'
                    })
                })

                return docDefinition
            }

            return new Promise((resolve, reject) => {
                const _nextTick = typeof this.$nextTick === 'function' ? this.$nextTick : (fn) => {
                    _.defer(fn)
                }
                _nextTick(() => {
                    const promises = []

                    promises.push(_imageToDataURL(options.logo))
                    if (!_pushSelectorPromises(selector, promises, this.$vuetify.theme.dark)) reject()

                    Promise.all(promises)
                        .then(results => {
                            const logo = _.first(results)
                            const dataUrls = _.drop(results)
                            const docDefinition = _buildDocDefinition(logo, dataUrls)
                            const pdf = pdfMake.createPdf(docDefinition)
                            if (options.send) {
                                pdf.getBase64(resolve)
                                return
                            }
                            options.print ? pdf.print() : options.open ? pdf.open() : pdf.download(filename)
                            resolve()
                        })
                        .catch(error => {
                            console.error('VueDomToImage: $domToPDF', error)
                            reject()
                        })
                })
            })
        }
    }
}

export default VueDomToImage

if (typeof window !== 'undefined' && window.Vue) window.Vue.use(VueDomToImage)
